import { createMemo } from 'solid-js';
import { createQuery, QueryClient, queryOptions } from '@tanstack/solid-query';

import { middleware } from '~/services/middleware';

import { getAppConfig } from '~/utils/appConfig';
import { idToken } from '../credentials';
import { profileToken } from '../profile';

export const queryClient = new QueryClient();

export function appConfigQuery() {
  return queryOptions({
    queryKey: ['appConfig'] as const,
    queryFn: getAppConfig,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
}

export function channelsQuery() {
  return queryOptions({
    queryKey: ['channels'] as const,
    queryFn: async () => {
      const {
        data: { channels },
      } = await middleware.content.v2.getChannels(false);
      return channels;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    enabled: !!idToken() && !!profileToken(),
  });
}

export function useChannel(channelId: () => string | undefined) {
  const channels = createQuery(() => channelsQuery());

  const channel = createMemo(() =>
    channelId() === undefined ? undefined : (
      channels.data?.find(({ channelId: id }) => id === channelId())
    ),
  );

  return {
    channel,
    isPending: () => channels.isPending,
  };
}

export function assetProgressQuery(
  assetId: string,
  assetType: string,
  vmsId: string,
  mainAssetId: string,
) {
  return queryOptions({
    queryKey: ['assetProgress', assetId] as const,
    queryFn: async ({ queryKey: [, assetIdFromQueryKey] }) => {
      let params;
      let service;

      switch (assetType) {
        case 'EPISODE': {
          params = mainAssetId || assetIdFromQueryKey;
          service = middleware.experience.v3.getContinueWatchingShow;
          break;
        }
        case 'MOVIE': {
          params = { vmsId, vrioAssetId: assetIdFromQueryKey };
          service = middleware.experience.v3.getContinueWatchingMovie;
          break;
        }
        default: {
          return {
            error: {
              data: 'No valid assetType specified',
            },
          };
        }
      }

      const { data } = await service(
        params as string & { vmsId: string; vrioAssetId: string },
      );

      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!idToken() && !!profileToken() && !!assetId,
  });
}

export function assetQuery(assetId: string) {
  return queryOptions({
    queryKey: ['asset', assetId] as const,
    queryFn: async ({ queryKey: [, assetIdFromQueryKey] }) => {
      const { data } =
        await middleware.content.v1.getAssetDetail(assetIdFromQueryKey);
      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !!idToken() && !!profileToken() && !!assetId,
  });
}

export function seasonQuery(seasonId: {
  showId: string;
  seasonNumber: number;
}) {
  return queryOptions({
    queryKey: ['season', seasonId] as const,
    queryFn: async ({ queryKey: [, { showId, seasonNumber }] }) => {
      const { data } = await middleware.content.v1.getSeasonDetail(
        showId,
        seasonNumber,
      );
      return data;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1.5 * 60 * 60 * 1000, // 1.5 hours
    enabled: !!idToken() && !!profileToken(),
  });
}
